<!-- Bell Button -->
<button
    mat-icon-button
    class="notifications-bell pf-header-icon-btn relative"
    (click)="openPanel()"
    #notificationsOrigin
    [attr.aria-label]="
        unreadCount > 0
            ? ('notifications.bellUnread' | transloco: { count: unreadCount })
            : ('notifications.bell' | transloco)
    "
    [matTooltip]="'notifications.bellTooltip' | transloco"
>
    @if (unreadCount > 0) {
        <span
            class="notifications-badge absolute right-0 top-0 flex items-center justify-center"
        >
            <span
                class="flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-xs font-semibold leading-none text-white shadow-sm"
                [class.bg-primary]="!hasNewRealtime"
                [class.bg-red-500]="hasNewRealtime"
                [class.notifications-badge--pulse]="hasNewRealtime"
            >
                {{ unreadCount > 99 ? '99+' : unreadCount }}
            </span>
        </span>
    }

    @if (hasNewRealtime) {
        <span
            class="pointer-events-none absolute inset-1 animate-ping rounded-full bg-primary-300"
            aria-hidden="true"
        ></span>
    }

    <mat-icon
        [svgIcon]="'heroicons_outline:bell'"
        class="text-current"
    ></mat-icon>
</button>

<!-- Notifications Panel -->
<ng-template #notificationsPanel>
    <div
        *transloco="let t"
        class="notifications-panel bg-card flex flex-col overflow-hidden shadow-2xl ring-1 ring-black/5"
        role="dialog"
        [attr.aria-label]="t('notifications.panelTitle')"
    >
        <!-- Header -->
        <div
            class="flex shrink-0 items-center gap-2 border-b border-zinc-100 px-4 py-3"
        >
            <button
                mat-icon-button
                class="sm:hidden"
                (click)="closePanel()"
                [attr.aria-label]="t('notifications.close')"
            >
                <mat-icon
                    class="icon-size-5"
                    [svgIcon]="'heroicons_solid:x-mark'"
                ></mat-icon>
            </button>

            <div class="min-w-0 flex-1">
                <div
                    class="text-base font-semibold leading-tight text-zinc-900"
                >
                    {{ t('notifications.panelTitle') }}
                </div>
                <div class="text-secondary mt-0.5 text-xs">
                    @if (isLoading && notifications.length === 0) {
                        {{ t('notifications.loading') }}
                    } @else if (unreadCount > 0) {
                        {{
                            t('notifications.summaryUnread', {
                                count: unreadCount,
                            })
                        }}
                    } @else {
                        {{ t('notifications.summaryClear') }}
                    }
                </div>
            </div>

            <button
                mat-icon-button
                class="text-secondary"
                [matTooltip]="t('notifications.markAllRead')"
                [disabled]="unreadCount === 0"
                (click)="markAllAsRead()"
                [attr.aria-label]="t('notifications.markAllRead')"
            >
                <mat-icon
                    class="icon-size-5"
                    [svgIcon]="'heroicons_outline:envelope-open'"
                ></mat-icon>
            </button>
        </div>

        <!-- Filter chips -->
        <div
            class="flex shrink-0 gap-2 border-b border-zinc-100 px-4 py-2"
        >
            <button
                type="button"
                class="notifications-chip"
                [class.notifications-chip--active]="panelFilter === 'all'"
                (click)="setPanelFilter('all')"
            >
                {{ t('notifications.filterAll') }}
            </button>
            <button
                type="button"
                class="notifications-chip"
                [class.notifications-chip--active]="panelFilter === 'unread'"
                (click)="setPanelFilter('unread')"
            >
                {{ t('notifications.filterUnread') }}
                @if (unreadCount > 0) {
                    <span class="notifications-chip-count">{{
                        unreadCount
                    }}</span>
                }
            </button>
        </div>

        <!-- Body -->
        <div
            class="relative flex flex-auto flex-col overflow-y-auto"
            [attr.aria-label]="t('notifications.listLabel')"
        >
            @if (isLoading && notifications.length === 0) {
                <div class="p-4">
                    <app-loading-state [height]="240" />
                </div>
            }

            @if (loadError && notifications.length === 0 && !isLoading) {
                <div
                    class="flex flex-col items-center justify-center px-6 py-12 text-center"
                >
                    <div
                        class="flex h-12 w-12 items-center justify-center rounded-full"
                        [ngClass]="loadErrorSurfaceClasses"
                    >
                        <mat-icon
                            [ngClass]="loadErrorIconClass"
                            [svgIcon]="'heroicons_outline:exclamation-triangle'"
                        ></mat-icon>
                    </div>
                    <p
                        class="mt-3 text-sm font-medium text-zinc-800"
                    >
                        {{ t('notifications.loadError') }}
                    </p>
                    <button
                        mat-stroked-button
                        class="mt-4"
                        type="button"
                        (click)="retry()"
                    >
                        {{ t('notifications.retry') }}
                    </button>
                </div>
            }

            @if (!isLoading || notifications.length > 0) {
                <ul class="m-0 list-none p-0">
                    @for (
                        notification of visibleNotifications;
                        track trackByFn($index, notification)
                    ) {
                        <li class="list-none">
                            <app-notification-row
                                [notification]="notification"
                                [dense]="true"
                                [divider]="!$last"
                                [severityLabel]="severityLabel(notification)"
                                (toggleRead)="toggleRead($event)"
                                (navigate)="navigateTo($event)"
                                (delete)="delete($event)"
                            />
                        </li>
                    }
                </ul>

                <!-- D22: the list is a page, not the whole history. Only the
                     unfiltered view can page — "Unread" filters the loaded page
                     client-side, so paging under it would be misleading. -->
                @if (hasMore && panelFilter === 'all') {
                    <div class="flex justify-center px-4 py-3">
                        <button
                            mat-stroked-button
                            type="button"
                            [disabled]="isLoading"
                            (click)="loadMore()"
                        >
                            {{ t('notifications.loadMore') }}
                        </button>
                    </div>
                }

                @if (visibleNotifications.length === 0 && !loadError) {
                    <output
                        class="flex flex-auto flex-col items-center justify-center px-8 py-14"
                        aria-live="polite"
                    >
                        <div
                            class="flex h-14 w-14 items-center justify-center rounded-full bg-primary-50"
                        >
                            <mat-icon
                                class="text-primary"
                                [svgIcon]="
                                    panelFilter === 'unread'
                                        ? 'heroicons_outline:check-circle'
                                        : 'heroicons_outline:bell-slash'
                                "
                            ></mat-icon>
                        </div>
                        <p
                            class="mt-4 text-base font-semibold text-zinc-800"
                        >
                            @if (panelFilter === 'unread') {
                                {{ t('notifications.emptyUnreadTitle') }}
                            } @else {
                                {{ t('notifications.emptyTitle') }}
                            }
                        </p>
                        <p
                            class="text-secondary mt-1 max-w-64 text-center text-sm"
                        >
                            @if (panelFilter === 'unread') {
                                {{ t('notifications.emptyUnreadBody') }}
                            } @else {
                                {{ t('notifications.emptyBody') }}
                            }
                        </p>
                    </output>
                }
            }
        </div>

        <!-- Footer -->
        <div
            class="shrink-0 border-t border-zinc-100 bg-zinc-50/80 px-4 py-2.5"
        >
            <button
                type="button"
                class="w-full rounded-lg py-2 text-center text-sm font-semibold text-primary transition-colors hover:bg-primary-50"
                (click)="viewAll()"
            >
                {{ t('notifications.viewAll') }}
            </button>
        </div>
    </div>
</ng-template>
